import { onCLS, onLCP, onFCP, onTTFB, onINP } from 'web-vitals';

/**
 * Performance Utilities for Core Web Vitals Monitoring
 *
 * Measures and reports key performance metrics:
 * - LCP (Largest Contentful Paint) - Loading performance
 * - INP (Interaction to Next Paint) - Responsiveness (replaced FID in 2024)
 * - CLS (Cumulative Layout Shift) - Visual stability
 * - FCP (First Contentful Paint) - Initial rendering
 * - TTFB (Time to First Byte) - Server response time
 */

// ============================================================================
// CORE WEB VITALS TRACKING
// ============================================================================

/**
 * Report Web Vitals to analytics endpoint
 * @param {Object} metric - Web Vital metric object
 */
const sendToAnalytics = (metric) => {
  const { name, value, id, rating, navigationType } = metric;

  // Log to console in development
  if (import.meta.env.DEV) {
    console.log(`[Web Vitals] ${name}:`, {
      value: Math.round(value),
      rating,
      id,
      navigationType,
    });
  }

  // Send to analytics service (PostHog, Google Analytics, etc.)
  if (window.posthog) {
    window.posthog.capture('web_vitals', {
      metric_name: name,
      value: Math.round(value),
      rating,
      metric_id: id,
      navigation_type: navigationType,
    });
  }

  // Send to custom analytics endpoint
  if (navigator.sendBeacon) {
    const body = JSON.stringify({
      name,
      value: Math.round(value),
      rating,
      id,
      navigationType,
      url: window.location.href,
      timestamp: Date.now(),
    });

    navigator.sendBeacon('/api/analytics/web-vitals', body);
  }
};

/**
 * Initialize Web Vitals tracking
 */
export const initWebVitals = () => {
  // Largest Contentful Paint (LCP)
  // Good: < 2.5s, Needs Improvement: 2.5s - 4s, Poor: > 4s
  onLCP(sendToAnalytics);

  // Cumulative Layout Shift (CLS)
  // Good: < 0.1, Needs Improvement: 0.1 - 0.25, Poor: > 0.25
  onCLS(sendToAnalytics);

  // First Contentful Paint (FCP)
  // Good: < 1.8s, Needs Improvement: 1.8s - 3s, Poor: > 3s
  onFCP(sendToAnalytics);

  // Time to First Byte (TTFB)
  // Good: < 800ms, Needs Improvement: 800ms - 1800ms, Poor: > 1800ms
  onTTFB(sendToAnalytics);

  // Interaction to Next Paint (INP) - Replaced FID as core metric in 2024
  // Good: < 200ms, Needs Improvement: 200ms - 500ms, Poor: > 500ms
  onINP(sendToAnalytics);
};

// ============================================================================
// PERFORMANCE MARKS & MEASURES
// ============================================================================

/**
 * Performance Mark for key user journeys
 */
export class PerformanceMark {
  constructor(name) {
    this.name = name;
    this.startMark = `${name}-start`;
    this.endMark = `${name}-end`;
    this.measureName = `${name}-duration`;
  }

  /**
   * Start timing
   */
  start() {
    if ('performance' in window && 'mark' in performance) {
      performance.mark(this.startMark);
    }
    return this;
  }

  /**
   * End timing and measure duration
   */
  end() {
    if ('performance' in window && 'mark' in performance && 'measure' in performance) {
      performance.mark(this.endMark);
      try {
        performance.measure(this.measureName, this.startMark, this.endMark);
        const measure = performance.getEntriesByName(this.measureName)[0];

        if (import.meta.env.DEV) {
          console.log(
            `[Performance] ${this.name}: ${Math.round(measure.duration)}ms`
          );
        }

        // Send to analytics
        if (window.posthog) {
          window.posthog.capture('performance_measure', {
            mark_name: this.name,
            duration: Math.round(measure.duration),
          });
        }

        return measure.duration;
      } catch (e) {
        console.warn('Performance measurement failed:', e);
      }
    }
    return null;
  }

  /**
   * Clear marks and measures
   */
  clear() {
    if ('performance' in window) {
      performance.clearMarks(this.startMark);
      performance.clearMarks(this.endMark);
      performance.clearMeasures(this.measureName);
    }
  }
}

/**
 * Measure page load time
 */
export const measurePageLoad = () => {
  if ('performance' in window && 'timing' in performance) {
    window.addEventListener('load', () => {
      const perfData = performance.timing;
      const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
      const connectTime = perfData.responseEnd - perfData.requestStart;
      const renderTime = perfData.domComplete - perfData.domLoading;

      if (import.meta.env.DEV) {
        console.log('[Performance] Page Load Metrics:', {
          pageLoadTime: `${pageLoadTime}ms`,
          connectTime: `${connectTime}ms`,
          renderTime: `${renderTime}ms`,
        });
      }

      // Send to analytics
      if (window.posthog) {
        window.posthog.capture('page_load_metrics', {
          page_load_time: pageLoadTime,
          connect_time: connectTime,
          render_time: renderTime,
        });
      }
    });
  }
};

/**
 * Measure time to interactive
 */
export const measureTimeToInteractive = () => {
  if ('PerformanceObserver' in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (import.meta.env.DEV) {
            console.log('[Performance] Long Task:', {
              duration: `${Math.round(entry.duration)}ms`,
              startTime: `${Math.round(entry.startTime)}ms`,
            });
          }
        }
      });

      observer.observe({ entryTypes: ['longtask'] });
    } catch (e) {
      console.warn('Long task monitoring not supported');
    }
  }
};

// ============================================================================
// DEBOUNCE & THROTTLE UTILITIES
// ============================================================================

/**
 * Debounce function - delays execution until after wait time
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @param {boolean} immediate - Execute on leading edge
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait = 300, immediate = false) => {
  let timeout;

  return function executedFunction(...args) {
    const context = this;

    const later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };

    const callNow = immediate && !timeout;

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);

    if (callNow) func.apply(context, args);
  };
};

/**
 * Throttle function - limits execution to once per wait time
 * @param {Function} func - Function to throttle
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, wait = 300) => {
  let inThrottle;
  let lastTime;

  return function executedFunction(...args) {
    const context = this;

    if (!inThrottle) {
      func.apply(context, args);
      lastTime = Date.now();
      inThrottle = true;

      setTimeout(() => {
        inThrottle = false;
      }, wait);
    }
  };
};

// ============================================================================
// REQUEST IDLE CALLBACK UTILITIES
// ============================================================================

/**
 * RequestIdleCallback with fallback for unsupported browsers
 * @param {Function} callback - Function to execute
 * @param {Object} options - Options object with timeout
 * @returns {number} Request ID
 */
export const requestIdleCallback =
  window.requestIdleCallback ||
  function (callback, options) {
    const start = Date.now();
    return setTimeout(() => {
      callback({
        didTimeout: false,
        timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
      });
    }, options?.timeout || 1);
  };

/**
 * CancelIdleCallback with fallback
 * @param {number} id - Request ID to cancel
 */
export const cancelIdleCallback = window.cancelIdleCallback || clearTimeout;

/**
 * Run low-priority task on idle
 * @param {Function} task - Task to run
 * @param {number} timeout - Timeout in milliseconds
 */
export const runOnIdle = (task, timeout = 2000) => {
  return requestIdleCallback(
    (deadline) => {
      if (deadline.timeRemaining() > 0 || deadline.didTimeout) {
        task();
      }
    },
    { timeout }
  );
};

// ============================================================================
// RESOURCE TIMING
// ============================================================================

/**
 * Get resource timing metrics
 */
export const getResourceTimings = () => {
  if ('performance' in window && 'getEntriesByType' in performance) {
    const resources = performance.getEntriesByType('resource');

    // Group by resource type
    const grouped = resources.reduce((acc, resource) => {
      const type = resource.initiatorType || 'other';
      if (!acc[type]) acc[type] = [];
      acc[type].push({
        name: resource.name,
        duration: Math.round(resource.duration),
        size: resource.transferSize,
      });
      return acc;
    }, {});

    // Calculate totals
    const totals = Object.entries(grouped).map(([type, items]) => ({
      type,
      count: items.length,
      totalDuration: items.reduce((sum, item) => sum + item.duration, 0),
      totalSize: items.reduce((sum, item) => sum + (item.size || 0), 0),
    }));

    if (import.meta.env.DEV) {
      console.table(totals);
    }

    return { grouped, totals };
  }
  return null;
};

/**
 * Monitor largest resource loads
 */
export const monitorLargeResources = (thresholdMB = 1) => {
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const sizeMB = entry.transferSize / (1024 * 1024);
        if (sizeMB > thresholdMB) {
          console.warn(`[Performance] Large resource detected:`, {
            url: entry.name,
            size: `${sizeMB.toFixed(2)}MB`,
            duration: `${Math.round(entry.duration)}ms`,
          });
        }
      }
    });

    observer.observe({ entryTypes: ['resource'] });
  }
};

// ============================================================================
// MEMORY MONITORING
// ============================================================================

/**
 * Get memory usage information (Chrome only)
 */
export const getMemoryInfo = () => {
  if ('memory' in performance) {
    const memory = performance.memory;
    return {
      usedJSHeapSize: (memory.usedJSHeapSize / (1024 * 1024)).toFixed(2),
      totalJSHeapSize: (memory.totalJSHeapSize / (1024 * 1024)).toFixed(2),
      jsHeapSizeLimit: (memory.jsHeapSizeLimit / (1024 * 1024)).toFixed(2),
    };
  }
  return null;
};

/**
 * Monitor memory usage periodically
 */
export const monitorMemory = (intervalMs = 30000) => {
  const checkMemory = () => {
    const memInfo = getMemoryInfo();
    if (memInfo && import.meta.env.DEV) {
      console.log('[Performance] Memory Usage:', memInfo);
    }
  };

  checkMemory(); // Initial check
  return setInterval(checkMemory, intervalMs);
};

// ============================================================================
// BUNDLE SIZE MONITORING
// ============================================================================

/**
 * Report bundle size metrics
 */
export const reportBundleSize = () => {
  if ('performance' in window) {
    runOnIdle(() => {
      const entries = performance.getEntriesByType('resource');
      const scripts = entries.filter((e) => e.initiatorType === 'script');
      const styles = entries.filter((e) => e.initiatorType === 'link');

      const scriptSize = scripts.reduce((sum, s) => sum + (s.transferSize || 0), 0);
      const styleSize = styles.reduce((sum, s) => sum + (s.transferSize || 0), 0);

      if (import.meta.env.DEV) {
        console.log('[Performance] Bundle Sizes:', {
          scripts: `${(scriptSize / 1024).toFixed(2)}KB`,
          styles: `${(styleSize / 1024).toFixed(2)}KB`,
          total: `${((scriptSize + styleSize) / 1024).toFixed(2)}KB`,
        });
      }

      // Send to analytics
      if (window.posthog) {
        window.posthog.capture('bundle_size', {
          script_size_kb: Math.round(scriptSize / 1024),
          style_size_kb: Math.round(styleSize / 1024),
        });
      }
    });
  }
};

// ============================================================================
// INITIALIZE PERFORMANCE MONITORING
// ============================================================================

/**
 * Initialize all performance monitoring
 */
export const initPerformanceMonitoring = () => {
  // Core Web Vitals
  initWebVitals();

  // Page load timing
  measurePageLoad();

  // Time to interactive
  measureTimeToInteractive();

  // Resource monitoring
  monitorLargeResources();

  // Bundle size reporting
  reportBundleSize();

  // Memory monitoring (dev only)
  if (import.meta.env.DEV) {
    monitorMemory();
  }
};

// Auto-initialize if in browser
if (typeof window !== 'undefined') {
  // Run initialization on next tick to avoid blocking
  runOnIdle(() => {
    initPerformanceMonitoring();
  });
}
